import React from 'react';
import Icon from '../../../components/AppIcon';

import { formatCurrency } from '../../../utils/currency';

const BudgetCard = ({ budget, onEdit, onDelete, getProgressColor, getProgressBarColor }) => {
  const progressPercentage = Math.min(budget?.percentageUsed, 100);
  const isOverBudget = budget?.percentageUsed > 100;

  return (
    <div className="bg-card rounded-lg border border-border p-20 md:p-24 shadow-elevation-2 hover:shadow-elevation-3 transition-smooth">
      {/* Category Header */}
      <div className="flex items-start justify-between mb-16">
        <div className="flex items-center gap-12">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: budget?.category?.color || '#6366f1' }}
          >
            <Icon name={budget?.category?.icon || 'Tag'} size={20} color="white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-card-foreground">
              {budget?.category?.name || 'Unknown Category'}
            </h3>
            <p className="text-sm text-muted-foreground">
              {budget?.isRecurring ? (
                <span className="flex items-center gap-4">
                  <Icon name="Repeat" size={14} color="var(--color-primary)" />
                  Recurring {budget?.recurrenceFrequency === 'monthly' ? 'Monthly' : budget?.recurrenceFrequency === 'quarterly' ? 'Quarterly' : 'Yearly'}
                </span>
              ) : (
                'Monthly Budget'
              )}
            </p>
          </div>
        </div>
        <div className="flex gap-8">
          <button
            onClick={onEdit}
            className="p-8 hover:bg-muted rounded-lg transition-smooth min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Edit budget"
          >
            <Icon name="Edit" size={18} color="var(--color-muted-foreground)" />
          </button>
          <button
            onClick={onDelete}
            className="p-8 hover:bg-destructive/10 rounded-lg transition-smooth min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Delete budget"
          >
            <Icon name="Trash2" size={18} color="var(--color-destructive)" />
          </button>
        </div>
      </div>
      {/* Budget Amounts */}
      <div className="mb-16">
        <div className="flex justify-between items-baseline mb-8">
          <span className="text-sm text-muted-foreground">Spent</span>
          <span className={`text-xl font-bold ${getProgressColor(budget?.percentageUsed, budget?.alertThreshold1, budget?.alertThreshold2)}`}>
            {formatCurrency(budget?.currentSpending)}
          </span>
        </div>
        <div className="flex justify-between items-baseline mb-8">
          <span className="text-sm text-muted-foreground">Limit</span>
          <span className="text-lg font-semibold text-foreground">
            {formatCurrency(budget?.limitAmount)}
          </span>
        </div>
        <div className="flex justify-between items-baseline">
          <span className="text-sm text-muted-foreground">Remaining</span>
          <span className={`text-lg font-semibold ${budget?.remainingBudget < 0 ? 'text-destructive' : 'text-success'}`}>
            {formatCurrency(Math.abs(budget?.remainingBudget))}
            {budget?.remainingBudget < 0 && ' over'}
          </span>
        </div>
      </div>
      {/* Progress Bar */}
      <div className="mb-12">
        <div className="w-full bg-muted rounded-full h-8 overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${getProgressBarColor(budget?.percentageUsed, budget?.alertThreshold1, budget?.alertThreshold2)}`}
            style={{ width: `${progressPercentage}%` }}
          ></div>
        </div>
        <div className="flex justify-between items-center mt-8">
          <span className={`text-sm font-medium ${getProgressColor(budget?.percentageUsed, budget?.alertThreshold1, budget?.alertThreshold2)}`}>
            {budget?.percentageUsed?.toFixed(1)}% used
          </span>
          {isOverBudget && (
            <span className="text-xs text-destructive font-medium flex items-center gap-4">
              <Icon name="AlertTriangle" size={14} />
              Over Budget
            </span>
          )}
        </div>
      </div>
      {/* Alert Thresholds */}
      <div className="pt-12 border-t border-border">
        <p className="text-xs text-muted-foreground mb-4">Alert Thresholds</p>
        <div className="flex gap-12">
          <span className="text-xs px-8 py-4 bg-warning/10 text-warning rounded">
            {budget?.alertThreshold1}%
          </span>
          <span className="text-xs px-8 py-4 bg-destructive/10 text-destructive rounded">
            {budget?.alertThreshold2}%
          </span>
        </div>
        {budget?.isRecurring && budget?.endDate && (
          <p className="text-xs text-muted-foreground mt-8">
            Ends: {new Date(budget?.endDate)?.toLocaleDateString()}
          </p>
        )}
      </div>
    </div>
  );
};

export default BudgetCard;